import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
    ArrowLeft,
    Flag,
    Plus,
    Sparkle,
    Trophy,
    Camera,
    Microphone,
    MicrophoneSlash,
} from '@phosphor-icons/react';
import { api, formatApiErrorDetail } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useAudioPrefs } from '@/hooks/useAudioPrefs';
import { cue as fireCue, unlockAudio } from '@/lib/audioCue';
import { ExerciseSessionCard } from '@/components/ExerciseSessionCard';
import { RestTimer } from '@/components/RestTimer';
import { ExercisePicker } from '@/components/ExercisePicker';
import { ScanMachine } from '@/components/ScanMachine';
import { RepPacer } from '@/components/RepPacer';
import { restForExercise } from '@/lib/exerciseCategory';
import { useVoiceCommand } from '@/hooks/useVoiceCommand';
import { useConfirm } from '@/components/ConfirmDialog';
import { SESSION, SCAN, CUES } from '@/constants/testIds';

function formatElapsed(ms) {
    const total = Math.max(0, Math.floor(ms / 1000));
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    if (h > 0) {
        return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    }
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export default function ActiveSession() {
    const { id: sessionId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const audioPrefs = useAudioPrefs();
    const units = user?.units || 'kg';

    const [session, setSession] = useState(null);
    const [sets, setSets] = useState([]);
    const [exerciseLookup, setExerciseLookup] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [restStartedAt, setRestStartedAt] = useState(null);
    const [restTargetSeconds, setRestTargetSeconds] = useState(null);
    const [restRestartKey, setRestRestartKey] = useState(0);
    const [pickerOpen, setPickerOpen] = useState(false);
    const [scanOpen, setScanOpen] = useState(false);
    const [pacingSet, setPacingSet] = useState(null);
    const [pacerAutoStart, setPacerAutoStart] = useState(false);
    const [paceModeActive, setPaceModeActive] = useState(false);
    const [micEnabled, setMicEnabled] = useState(false);
    const [finishing, setFinishing] = useState(false);
    const { confirm, dialog: confirmDialog } = useConfirm();

    // Local timer tick for the session elapsed time.
    const [now, setNow] = useState(Date.now());
    useEffect(() => {
        const t = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(t);
    }, []);

    // Per-exercise "last completed at" — used to derive rest_seconds when the
    // next set in the same exercise is completed.
    const lastCompletedByExerciseRef = useRef({});

    const load = useCallback(async () => {
        setError('');
        try {
            const [sessRes, setsRes, exRes] = await Promise.all([
                api.get(`/sessions/${sessionId}`),
                api.get(`/sessions/${sessionId}/sets`),
                api.get('/exercises'),
            ]);
            setSession(sessRes.data);
            setSets(setsRes.data);
            const lookup = {};
            exRes.data.forEach((e) => {
                lookup[e.id] = e;
            });
            setExerciseLookup(lookup);
        } catch (e) {
            setError(
                formatApiErrorDetail(e.response?.data?.detail) || e.message
            );
            setSession(false);
        } finally {
            setLoading(false);
        }
    }, [sessionId]);

    useEffect(() => {
        load();
    }, [load]);

    // Group sets by exercise_id, preserving the order in which each exercise
    // first appears in the sets list.
    const grouped = useMemo(() => {
        const order = [];
        const byEx = new Map();
        sets.forEach((s) => {
            if (!byEx.has(s.exercise_id)) {
                byEx.set(s.exercise_id, []);
                order.push(s.exercise_id);
            }
            byEx.get(s.exercise_id).push(s);
        });
        byEx.forEach((arr) =>
            arr.sort((a, b) => a.set_number - b.set_number)
        );
        return order.map((id) => ({ exerciseId: id, sets: byEx.get(id) }));
    }, [sets]);

    const totals = useMemo(() => {
        const completed = sets.filter((s) => s.completed && !s.is_warmup);
        const volume = completed.reduce(
            (acc, s) => acc + Number(s.weight || 0) * Number(s.reps || 0),
            0
        );
        const prs = sets.filter((s) => s.is_pr).length;
        return { completedCount: completed.length, volume, prs };
    }, [sets]);

    const isReadOnly = !!session?.completed_at;
    const elapsedMs = session?.started_at
        ? now - new Date(session.started_at).getTime()
        : 0;

    // Session-start cue — fires ONCE per session-id (tracked in
    // sessionStorage so a reload of an in-progress session doesn't repeat
    // the welcome). Suppressed for read-only sessions and when cue_mode=off.
    const startCueFiredRef = useRef(false);
    useEffect(() => {
        if (startCueFiredRef.current) return;
        if (!session || isReadOnly || grouped.length === 0) return;
        if (Object.keys(exerciseLookup).length === 0) return;

        const key = `gymflow-session-start-${sessionId}`;
        try {
            if (window.sessionStorage.getItem(key)) {
                startCueFiredRef.current = true;
                return;
            }
        } catch {
            /* ignore */
        }

        const firstGroup = grouped[0];
        const firstEx = exerciseLookup[firstGroup.exerciseId];
        if (!firstEx) return;
        const setsCount = firstGroup.sets.length;
        const firstSet = firstGroup.sets[0];
        const targetReps = firstSet?.reps || null;

        const reps = targetReps ? `, ${targetReps} reps each` : '';
        const text = `Workout begins. First up, ${firstEx.name}, ${setsCount} ${
            setsCount === 1 ? 'set' : 'sets'
        }${reps}.`;

        // Best-effort unlock — must follow a user gesture to actually sound.
        // The session was opened via a tap on a workout-start button so the
        // gesture chain is usually live; if not, the browser silently skips.
        try {
            unlockAudio();
            fireCue('session_start', { target: text });
        } catch {
            /* ignore */
        }
        try {
            window.sessionStorage.setItem(key, '1');
        } catch {
            /* ignore */
        }
        startCueFiredRef.current = true;
    }, [session, isReadOnly, grouped, exerciseLookup, sessionId]);

    // Voice commands — wired up only when the user has the toggle on in
    // their profile AND has also tapped the mic toggle for this session.
    const nextIncompleteSet = useMemo(
        () => sets.find((s) => !s.completed && !s.is_warmup),
        [sets]
    );
    const handleCompleteSet = useCallback(
        (setObj, commit) => {
            // Once the user has paced one set, subsequent ✓ taps open the
            // pacer with autoStart=true so the workout flows hands-free.
            // Skip warmups (no pacing) and 0-rep sets (e.g. cardio).
            if (
                paceModeActive &&
                !setObj.is_warmup &&
                (setObj.reps || 0) > 0
            ) {
                setPacerAutoStart(true);
                setPacingSet(setObj);
                return Promise.resolve();
            }
            return completeSet(setObj, commit);
        },
        [paceModeActive, completeSet]
    );

    const handleVoiceCommand = useCallback(
        (cmd) => {
            if (cmd === 'start') {
                if (!pacingSet && nextIncompleteSet) {
                    setPacingSet(nextIncompleteSet);
                }
            } else if (cmd === 'next') {
                const target = pacingSet || nextIncompleteSet;
                if (!target) return;
                setPacingSet(null);
                completeSet(target, {
                    weight: target.weight,
                    reps: target.reps,
                    rpe:
                        target.rpe === '' || target.rpe == null
                            ? null
                            : Number(target.rpe),
                    is_warmup: !!target.is_warmup,
                });
            } else if (cmd === 'stop') {
                setPacingSet(null);
            }
        },
        [pacingSet, nextIncompleteSet]
    );
    const voiceEnabled = !!user?.workout_cue_prefs?.voice_command_enabled;
    const voice = useVoiceCommand({
        enabled: voiceEnabled && micEnabled,
        onCommand: handleVoiceCommand,
    });

    function applyLocalSet(id, patch) {
        setSets((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s)));
    }

    async function patchSet(setObj, patch) {
        applyLocalSet(setObj.id, patch);
        try {
            const { data } = await api.patch(
                `/sessions/${sessionId}/sets/${setObj.id}`,
                patch
            );
            applyLocalSet(setObj.id, data);
        } catch (e) {
            setError(
                formatApiErrorDetail(e.response?.data?.detail) || e.message
            );
            await load();
        }
    }

    async function completeSet(setObj, commit) {
        // Capture rest_seconds for the set that's about to be marked complete
        // based on the gap since the previous completed set in the same
        // exercise. (Wrist-watch heuristic — feels right at the gym.)
        const lastTs =
            lastCompletedByExerciseRef.current[setObj.exercise_id] || null;
        const restSeconds = lastTs
            ? Math.max(0, Math.round((Date.now() - lastTs) / 1000))
            : null;

        const patch = {
            weight: commit.weight,
            reps: commit.reps,
            rpe: commit.rpe,
            is_warmup: commit.is_warmup,
            completed: true,
            rest_seconds: restSeconds,
        };

        // Optimistic UI
        applyLocalSet(setObj.id, {
            ...patch,
            completed_at: new Date().toISOString(),
        });
        setRestStartedAt(Date.now());
        // Use this set's `rest_seconds` as the next-rest target if the user
        // set one (overrides the global default). Otherwise pick from the
        // user's profile based on whether this is a compound vs isolation lift,
        // and fall back to the audio-prefs default if profile is missing.
        const ex = exerciseLookup[setObj.exercise_id];
        const profileRest = user?.workout_cue_prefs
            ? restForExercise(ex, user.workout_cue_prefs)
            : null;
        const target =
            Number.isFinite(setObj.rest_seconds) && setObj.rest_seconds > 0
                ? setObj.rest_seconds
                : profileRest || audioPrefs.defaultRestSeconds;
        setRestTargetSeconds(target);
        setRestRestartKey((k) => k + 1);
        lastCompletedByExerciseRef.current[setObj.exercise_id] = Date.now();
        unlockAudio();
        fireCue('set_complete', { mode: audioPrefs.mode, target });

        // If this completion finishes the current exercise (no more incomplete
        // non-warmup sets), fire a transition cue announcing the next one.
        // Delayed so it doesn't overlap with set_complete / rep-count speech.
        const remainingInEx = sets.filter(
            (s) =>
                s.exercise_id === setObj.exercise_id &&
                !s.is_warmup &&
                !s.completed &&
                s.id !== setObj.id
        ).length;
        if (remainingInEx === 0) {
            const currentIdx = grouped.findIndex(
                (g) => g.exerciseId === setObj.exercise_id
            );
            const nextGroup = grouped
                .slice(currentIdx + 1)
                .find((g) =>
                    g.sets.some(
                        (s) =>
                            !s.is_warmup && !s.completed && s.id !== setObj.id
                    )
                );
            if (nextGroup) {
                const nextEx = exerciseLookup[nextGroup.exerciseId];
                const incompleteSets = nextGroup.sets.filter(
                    (s) => !s.is_warmup && !s.completed
                );
                const setsCount = incompleteSets.length;
                const firstReps = incompleteSets[0]?.reps || null;
                const repsText = firstReps
                    ? `, ${firstReps} reps`
                    : '';
                const transitionText = nextEx
                    ? `Nice. Next up, ${nextEx.name}, ${setsCount} ${
                          setsCount === 1 ? 'set' : 'sets'
                      }${repsText}.`
                    : 'Nice. Next exercise.';
                setTimeout(() => {
                    fireCue('exercise_transition', { target: transitionText });
                }, 1500);
            }
        }

        try {
            const { data } = await api.patch(
                `/sessions/${sessionId}/sets/${setObj.id}`,
                patch
            );
            applyLocalSet(setObj.id, data);
        } catch (e) {
            setError(
                formatApiErrorDetail(e.response?.data?.detail) || e.message
            );
            await load();
        }
    }

    async function uncompleteSet(setObj) {
        applyLocalSet(setObj.id, { completed: false, is_pr: false });
        try {
            const { data } = await api.patch(
                `/sessions/${sessionId}/sets/${setObj.id}`,
                { completed: false }
            );
            applyLocalSet(setObj.id, data);
        } catch (e) {
            setError(
                formatApiErrorDetail(e.response?.data?.detail) || e.message
            );
            await load();
        }
    }

    async function deleteSet(setObj) {
        const ok = await confirm({
            title: 'Delete this set?',
            description:
                'It will be removed from this workout immediately and can\'t be recovered.',
            confirmLabel: 'Delete set',
            tone: 'danger',
        });
        if (!ok) return;
        setSets((prev) => prev.filter((s) => s.id !== setObj.id));
        try {
            await api.delete(`/sessions/${sessionId}/sets/${setObj.id}`);
        } catch (e) {
            setError(
                formatApiErrorDetail(e.response?.data?.detail) || e.message
            );
            await load();
        }
    }

    async function addSet(exerciseId) {
        const inGroup = sets
            .filter((s) => s.exercise_id === exerciseId)
            .sort((a, b) => b.set_number - a.set_number);
        const seed = inGroup[0];
        const setNumber = seed ? seed.set_number + 1 : 1;
        const payload = {
            exercise_id: exerciseId,
            set_number: setNumber,
            weight: seed?.weight ?? 0,
            reps: seed?.reps ?? 0,
            is_warmup: false,
            completed: false,
        };
        try {
            const { data } = await api.post(
                `/sessions/${sessionId}/sets`,
                payload
            );
            setSets((prev) => [...prev, data]);
        } catch (e) {
            setError(
                formatApiErrorDetail(e.response?.data?.detail) || e.message
            );
        }
    }

    async function addExercises(picks) {
        for (const ex of picks) {
            await addSet(ex.id);
            setExerciseLookup((prev) => ({ ...prev, [ex.id]: ex }));
        }
    }

    async function addScannedExercise(exercise, weight) {
        // Add the new exercise to the session, then if the scan captured a
        // weight reading, PATCH that first shell so the user lands on a row
        // that's already pre-filled.
        setExerciseLookup((prev) => ({ ...prev, [exercise.id]: exercise }));
        try {
            const payload = {
                exercise_id: exercise.id,
                set_number: 1,
                weight: Number.isFinite(weight) ? weight : 0,
                reps: 0,
                is_warmup: false,
                completed: false,
            };
            const { data } = await api.post(
                `/sessions/${sessionId}/sets`,
                payload
            );
            setSets((prev) => [...prev, data]);
        } catch (e) {
            setError(
                formatApiErrorDetail(e.response?.data?.detail) || e.message
            );
        }
    }

    async function finish() {
        if (finishing) return;
        const ok = await confirm({
            title: 'Finish this workout?',
            description:
                "You'll see your recap with PRs and volume, and the session will move to your history.",
            confirmLabel: 'Finish workout',
            cancelLabel: 'Keep lifting',
            tone: 'primary',
        });
        if (!ok) return;
        setFinishing(true);
        try {
            await api.patch(`/sessions/${sessionId}`, {
                completed_at: new Date().toISOString(),
            });
            navigate(`/sessions/${sessionId}/recap`, { replace: true });
        } catch (e) {
            setError(
                formatApiErrorDetail(e.response?.data?.detail) || e.message
            );
        } finally {
            setFinishing(false);
        }
    }

    if (loading) {
        return (
            <main className="mx-auto max-w-3xl px-5 sm:px-8 pt-16 text-center">
                <p className="uppercase-mono text-muted-foreground animate-soft-pulse">
                    Loading session…
                </p>
            </main>
        );
    }
    if (!session) {
        return (
            <main
                data-testid={SESSION.notFound}
                className="mx-auto max-w-3xl px-5 sm:px-8 pt-16 text-center"
            >
                <p className="uppercase-mono text-muted-foreground">
                    Session not found.
                </p>
                <Link
                    to="/workouts"
                    className="inline-flex items-center gap-1.5 mt-4 uppercase-mono text-foreground hover:underline"
                >
                    <ArrowLeft size={14} weight="bold" /> Back to workouts
                </Link>
            </main>
        );
    }

    return (
        <main
            data-testid={SESSION.root}
            className="mx-auto max-w-3xl px-5 sm:px-8 pt-10 sm:pt-14 pb-32"
        >
            <div className="animate-fade-up">
                <Link
                    to="/workouts"
                    data-testid={SESSION.backToWorkouts}
                    className="inline-flex items-center gap-1.5 uppercase-mono text-muted-foreground hover:text-foreground transition-colors"
                >
                    <ArrowLeft size={14} weight="bold" />
                    Back to workouts
                </Link>
                <div className="mt-3 flex flex-wrap items-start justify-between gap-4">
                    <div className="min-w-0">
                        <p className="uppercase-mono text-muted-foreground">
                            {isReadOnly ? 'Completed session' : 'Active session'}
                        </p>
                        <h1
                            data-testid={SESSION.title}
                            className="font-display font-extrabold tracking-tighter text-4xl sm:text-5xl truncate"
                        >
                            {session.name}
                        </h1>
                    </div>
                    {!isReadOnly && (
                        <div className="flex items-center gap-2 flex-wrap">
                            {user?.workout_cue_prefs?.enable_rep_pacing && (
                                <button
                                    type="button"
                                    onClick={() => setPaceModeActive((v) => !v)}
                                    aria-pressed={paceModeActive}
                                    data-testid="pace-mode-toggle"
                                    title={
                                        paceModeActive
                                            ? 'Pace mode ON — ✓ opens the pacer'
                                            : 'Pace mode OFF — tap to chain pacer on every set'
                                    }
                                    className={`uppercase-mono inline-flex items-center gap-1.5 h-10 px-3 rounded-2xl border transition-all active:scale-[0.97] ${
                                        paceModeActive
                                            ? 'border-[#00FF9D]/50 bg-[#00FF9D]/15 text-[#00FF9D]'
                                            : 'border-foreground/15 bg-foreground/5 text-muted-foreground hover:text-foreground'
                                    }`}
                                >
                                    <Sparkle size={13} weight="fill" />
                                    {paceModeActive ? 'Pace · ON' : 'Pace · Off'}
                                </button>
                            )}
                            {voiceEnabled && (
                                <button
                                    type="button"
                                    onClick={() => setMicEnabled((v) => !v)}
                                    aria-pressed={micEnabled}
                                    aria-label={
                                        micEnabled
                                            ? 'Disable voice commands'
                                            : 'Enable voice commands'
                                    }
                                    title={
                                        voice.error
                                            ? voice.error
                                            : voice.supported
                                              ? micEnabled
                                                  ? 'Listening — say start, next, or stop'
                                                  : 'Tap to listen for voice commands'
                                              : 'Voice commands not supported in this browser'
                                    }
                                    disabled={!voice.supported}
                                    data-testid={CUES.voiceMicToggle}
                                    className={`grid place-items-center w-12 h-12 rounded-2xl border transition-all active:scale-[0.96] disabled:opacity-50 ${
                                        micEnabled && voice.listening
                                            ? 'border-[#00FF9D]/40 bg-[#00FF9D]/15 text-[#00FF9D] animate-soft-pulse'
                                            : 'border-foreground/15 bg-foreground/5 hover:bg-foreground/10'
                                    }`}
                                >
                                    {micEnabled ? (
                                        <Microphone size={18} weight="fill" />
                                    ) : (
                                        <MicrophoneSlash size={18} weight="bold" />
                                    )}
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={finish}
                                disabled={finishing}
                                data-testid={SESSION.finishBtn}
                                className="inline-flex items-center gap-2 h-12 px-5 rounded-2xl bg-foreground text-background font-bold tracking-tight hover:opacity-90 active:scale-[0.98] transition-all disabled:opacity-60"
                            >
                                <Flag size={16} weight="fill" />
                                {finishing ? 'Finishing…' : 'Finish'}
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Session totals strip */}
            <section className="mt-6 grid grid-cols-3 gap-3">
                <Total
                    label="Elapsed"
                    value={
                        <span data-testid={SESSION.elapsed}>
                            {formatElapsed(elapsedMs)}
                        </span>
                    }
                />
                <Total
                    label="Completed sets"
                    value={totals.completedCount}
                />
                <Total
                    label="PRs"
                    value={
                        <span className="inline-flex items-center gap-1">
                            {totals.prs}
                            {totals.prs > 0 && (
                                <Trophy
                                    size={16}
                                    weight="fill"
                                    className="text-[#FF4D4D]"
                                />
                            )}
                        </span>
                    }
                />
            </section>

            {error && (
                <p className="mt-5 text-sm text-[#FF4D4D] font-medium">
                    {error}
                </p>
            )}

            {/* Exercises */}
            <section className="mt-8 space-y-5">
                {grouped.length === 0 ? (
                    <div className="glass-card rounded-3xl p-10 text-center">
                        <span className="grid place-items-center w-12 h-12 rounded-2xl bg-foreground/5 border border-foreground/10 mx-auto mb-4">
                            <Sparkle size={20} weight="fill" />
                        </span>
                        <h3 className="font-display font-bold text-lg tracking-tight">
                            No exercises yet
                        </h3>
                        <p className="text-muted-foreground text-sm mt-1 max-w-xs mx-auto">
                            Add exercises to start logging sets.
                        </p>
                    </div>
                ) : (
                    grouped.map((g) => (
                        <ExerciseSessionCard
                            key={g.exerciseId}
                            exercise={exerciseLookup[g.exerciseId]}
                            sets={g.sets}
                            units={units}
                            disabled={isReadOnly}
                            onPatchSet={patchSet}
                            onCompleteSet={handleCompleteSet}
                            onUncompleteSet={uncompleteSet}
                            onDeleteSet={deleteSet}
                            onAddSet={addSet}
                            onStartPacer={
                                user?.workout_cue_prefs?.enable_rep_pacing
                                    ? (setObj) => setPacingSet(setObj)
                                    : undefined
                            }
                        />
                    ))
                )}
            </section>

            {!isReadOnly && (
                <div className="mt-6 grid sm:grid-cols-2 gap-3">
                    <button
                        type="button"
                        onClick={() => setPickerOpen(true)}
                        data-testid={SESSION.addExerciseBtn}
                        className="h-14 rounded-2xl border border-dashed border-foreground/20 text-muted-foreground hover:text-foreground hover:border-foreground/40 active:scale-[0.99] transition-all inline-flex items-center justify-center gap-2 font-semibold"
                    >
                        <Plus size={18} weight="bold" /> Add from catalog
                    </button>
                    <button
                        type="button"
                        onClick={() => setScanOpen(true)}
                        data-testid={SCAN.triggerInSession}
                        className="h-14 rounded-2xl bg-[#00FF9D]/10 border border-[#00FF9D]/30 text-[#00FF9D] hover:bg-[#00FF9D]/15 active:scale-[0.99] transition-all inline-flex items-center justify-center gap-2 font-semibold"
                    >
                        <Camera size={18} weight="bold" /> Scan a machine
                    </button>
                </div>
            )}

            {!isReadOnly && (
                <RestTimer
                    startedAt={restStartedAt}
                    targetSeconds={restTargetSeconds || undefined}
                    restartKey={restRestartKey}
                    onReset={() => setRestStartedAt(null)}
                />
            )}

            {confirmDialog}
            <ExercisePicker
                open={pickerOpen}
                onClose={() => setPickerOpen(false)}
                onAdd={addExercises}
            />
            <ScanMachine
                open={scanOpen}
                onClose={() => setScanOpen(false)}
                onCommitted={addScannedExercise}
                autofillContext={{ units }}
            />
            <RepPacer
                open={!!pacingSet}
                targetReps={pacingSet?.reps || 1}
                tempo={user?.workout_cue_prefs}
                autoStart={pacerAutoStart}
                onCancel={() => {
                    setPacingSet(null);
                    setPacerAutoStart(false);
                }}
                onComplete={async (repsDone) => {
                    const target = pacingSet;
                    setPacingSet(null);
                    setPacerAutoStart(false);
                    if (!target) return;
                    // First successful pace flips pace-mode on so subsequent
                    // ✓ buttons auto-open the pacer (flow-through workout).
                    setPaceModeActive(true);
                    await completeSet(target, {
                        weight: target.weight,
                        reps: repsDone || target.reps,
                        rpe:
                            target.rpe === '' || target.rpe == null
                                ? null
                                : Number(target.rpe),
                        is_warmup: !!target.is_warmup,
                    });
                }}
            />
        </main>
    );
}

function Total({ label, value }) {
    return (
        <div className="rounded-2xl border border-foreground/10 bg-foreground/[0.02] px-3 py-3">
            <p className="uppercase-mono text-muted-foreground mb-0.5">
                {label}
            </p>
            <p className="font-mono text-2xl font-bold tabular-nums tracking-tight">
                {value}
            </p>
        </div>
    );
}
